<script setup lang="ts">
import { Camera, ChevronLeft, Loader2, UserX } from "@lucide/vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";

import { useSectionDeepLink } from "../model/useSectionDeepLink";

import type { Master } from "@/entities/master";
import { MASTER_SECTION_IDS, masterFullName } from "@/entities/master";
import { MasterPortfolioPanel, MasterProfileForm, MasterServicesPanel } from "@/features";
import { useMasterPhoto } from "@/features/master-photo";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormAlert,
  Skeleton,
} from "@/shared/ui";

import { useMasterDetail } from "../model/useMasterDetail";
import DangerZoneCard from "./DangerZoneCard.vue";

/**
 * Хост сторінки майстра `/masters/:id` (spec improvement-090). Single-scroll:
 * аватар із camera-badge → профіль → послуги → розклад → небезпечна зона.
 * Вкладки (Tabs) видалено. Upload-логіка фото вбудована в identity-хедер через
 * `useMasterPhoto`; `DangerZoneCard` тримає флоу видалення майстра.
 */
const props = defineProps<{ id: string }>();

const emit = defineEmits<{
  deleted: [id: string];
}>();

const { t } = useI18n();

const { status, master, load, setMaster, patchPhoto, markNotFound } = useMasterDetail();

useSectionDeepLink(status, MASTER_SECTION_IDS);

const politeMessage = ref("");
const notFoundPanelRef = ref<HTMLElement | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);

const photo = useMasterPhoto(props.id, {
  onChanged: (url) => {
    patchPhoto(url);
    politeMessage.value = url === null
      ? t("masters.photo.a11y.removed")
      : t("masters.photo.a11y.uploaded");
  },
  onNotFound: () => markNotFound(),
});

const fullName = computed(() =>
  master.value ? masterFullName(master.value) : "",
);

/**
 * Джерело прев'ю: object-URL обраного файлу під час upload (не помилки);
 * інакше — photo_url майстра з сервера.
 */
const displayPhotoUrl = computed(() =>
  photo.status.value !== "error" && photo.previewUrl.value
    ? photo.previewUrl.value
    : (master.value?.photoUrl ?? null),
);

const isRemoveDialogOpen = computed(() => photo.removeState.value !== "idle");

async function focusNotFound(): Promise<void> {
  await nextTick();
  notFoundPanelRef.value?.focus();
}

watch(status, (s) => {
  if (s === "notFound") {
    void focusNotFound();
  }
});

function onProfileSaved(updated: Master): void {
  setMaster(updated);
  politeMessage.value = t("masters.a11y.savedAnnounce", {
    name: masterFullName(updated),
  });
}

function onProfileDeleted(id: string): void {
  emit("deleted", id);
}

function onSectionNotFound(): void {
  markNotFound();
}

function onFileChange(e: Event): void {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) {
    return;
  }
  photo.selectFile(file);
  // Негайний upload — без explicit-confirm у хедері (спрощений UX).
  void photo.confirmUpload();
  // Скинути input, щоб той самий файл можна було повторно обрати після помилки.
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
}

function onRemoveOpenChange(open: boolean): void {
  if (!open && photo.removeState.value !== "removing") {
    photo.cancelRemove();
  }
}

onMounted(() => {
  void load(props.id);
});
</script>

<template>
  <div class="mx-auto max-w-content">
    <RouterLink
      :to="{ name: 'masters' }"
      class="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ChevronLeft class="size-4" />
      {{ t("masters.backToList") }}
    </RouterLink>

    <!-- in-place polite статус-регіон (success збереження / фото) -->
    <p
      class="sr-only"
      role="status"
      aria-live="polite"
    >
      {{ politeMessage }}
    </p>

    <!-- loading: skeleton хедера + секцій -->
    <div
      v-if="status === 'loading'"
      data-testid="master-host-loading"
      aria-busy="true"
      class="flex flex-col gap-6"
    >
      <span class="sr-only">{{ t("masters.loading") }}</span>
      <div class="flex items-center gap-4">
        <Skeleton class="size-12 rounded-full" />
        <Skeleton class="h-6 w-48" />
      </div>
      <Skeleton class="h-40 w-full rounded-lg" />
      <Skeleton class="h-40 w-full rounded-lg" />
    </div>

    <!-- notFound: панель «майстра вже немає» -->
    <div
      v-else-if="status === 'notFound'"
      ref="notFoundPanelRef"
      data-testid="master-not-found"
      role="alert"
      tabindex="-1"
      class="flex flex-col items-center gap-3 rounded-lg border border-border bg-card py-12 text-center outline-none"
    >
      <UserX
        class="size-8 text-muted-foreground"
        aria-hidden="true"
      />
      <p class="font-medium text-foreground">
        {{ t("masters.notFound.title") }}
      </p>
      <p class="max-w-sm text-sm text-muted-foreground">
        {{ t("masters.notFound.body") }}
      </p>
      <Button as-child>
        <RouterLink :to="{ name: 'masters' }">
          {{ t("masters.notFound.backCta") }}
        </RouterLink>
      </Button>
    </div>

    <!-- error: мережева/серверна помилка + retry -->
    <div
      v-else-if="status === 'error'"
      data-testid="master-host-error"
      class="flex flex-col gap-3"
    >
      <FormAlert
        variant="destructive"
        :message="t('masters.loadError')"
      />
      <div>
        <Button
          variant="outline"
          size="sm"
          @click="load(id)"
        >
          {{ t("masters.retry") }}
        </Button>
      </div>
    </div>

    <!-- ready: identity header + картки секцій -->
    <div
      v-else
      class="flex flex-col gap-6"
    >
      <!-- Identity header: клікабельний аватар + ПІБ -->
      <div
        class="flex items-start gap-4"
        data-testid="master-identity"
      >
        <div class="relative flex-none">
          <button
            class="relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            :aria-label="t('masters.photo.a11y.changeButton')"
            :aria-busy="photo.status.value === 'uploading'"
            :disabled="photo.status.value === 'uploading'"
            data-testid="avatar-button"
            type="button"
            @click="fileInputRef?.click()"
          >
            <Avatar
              :name="fullName"
              :src="displayPhotoUrl"
              size="lg"
              aria-hidden="true"
            />
            <!-- Camera badge: завжди видима (не hover-only, mobile-first) -->
            <span
              class="absolute bottom-0 right-0 flex size-5 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border"
              aria-hidden="true"
              data-testid="camera-badge"
            >
              <Camera class="size-3 text-muted-foreground" />
            </span>
            <!-- Upload spinner overlay -->
            <div
              v-if="photo.status.value === 'uploading'"
              class="absolute inset-0 flex items-center justify-center rounded-full bg-background/60"
              aria-hidden="true"
            >
              <Loader2 class="size-5 animate-spin text-foreground/70" />
            </div>
          </button>

          <!-- Hidden file input — тригерується кнопкою аватара -->
          <input
            ref="fileInputRef"
            type="file"
            accept="image/jpeg,image/png"
            class="sr-only"
            aria-hidden="true"
            tabindex="-1"
            @change="onFileChange"
          >
        </div>

        <!-- ПІБ та «Прибрати фото» -->
        <div class="flex min-w-0 flex-1 flex-col gap-1 pt-1">
          <h2 class="min-w-0 truncate text-lg font-semibold text-foreground">
            {{ fullName }}
          </h2>
          <button
            v-if="master?.photoUrl"
            type="button"
            class="self-start text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="remove-photo-link"
            @click="photo.requestRemove()"
          >
            {{ t("masters.photo.actions.remove") }}
          </button>
        </div>
      </div>

      <!-- Upload error inline (прев'ю аватара лишається, S6) -->
      <FormAlert
        v-if="photo.errorKey.value"
        variant="destructive"
        :message="t(photo.errorKey.value)"
        data-testid="photo-upload-error"
      />

      <!-- Remove photo confirm dialog (дзеркалить паттерн MasterPhotoPanel) -->
      <AlertDialog
        :open="isRemoveDialogOpen"
        @update:open="onRemoveOpenChange"
      >
        <AlertDialogContent data-testid="photo-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{{ t("masters.photo.remove.title") }}</AlertDialogTitle>
            <AlertDialogDescription>
              {{ t("masters.photo.remove.description") }}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <FormAlert
            v-if="photo.removeErrorKey.value"
            data-testid="photo-remove-error"
            variant="destructive"
            :message="t(photo.removeErrorKey.value)"
          />

          <AlertDialogFooter>
            <AlertDialogCancel>{{ t("masters.photo.actions.cancel") }}</AlertDialogCancel>
            <Button
              data-testid="photo-remove-confirm"
              variant="destructive"
              :loading="photo.removeState.value === 'removing'"
              @click="photo.confirmRemove()"
            >
              {{ t("masters.photo.remove.confirm") }}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <!-- Профіль -->
      <Card data-testid="section-profile">
        <CardHeader>
          <CardTitle>{{ t("masters.sections.profile") }}</CardTitle>
        </CardHeader>
        <CardContent>
          <MasterProfileForm
            :id="id"
            :key="id"
            mode="edit"
            @saved="onProfileSaved"
            @not-found="onSectionNotFound"
          />
        </CardContent>
      </Card>

      <!-- Послуги -->
      <Card
        id="services"
        data-testid="section-services"
      >
        <CardHeader>
          <CardTitle
            tabindex="-1"
            data-section-heading
          >
            {{ t("masters.sections.services") }}
          </CardTitle>
        </CardHeader>
        <CardContent class="p-0">
          <MasterServicesPanel
            :master-id="id"
            @not-found="onSectionNotFound"
          />
        </CardContent>
      </Card>

      <!-- Портфоліо -->
      <Card data-testid="section-portfolio">
        <CardHeader>
          <CardTitle>{{ t("masters.sections.portfolio") }}</CardTitle>
        </CardHeader>
        <CardContent class="p-0">
          <MasterPortfolioPanel
            :master-id="id"
            @not-found="onSectionNotFound"
          />
        </CardContent>
      </Card>

      <!-- Небезпечна зона -->
      <DangerZoneCard
        :master-id="id"
        :master-name="fullName"
        data-testid="section-danger-zone"
        @deleted="onProfileDeleted"
      />
    </div>
  </div>
</template>

<style scoped>
/* Scroll anchor offset — must clear the sticky AppLayout header (h-14 = 3.5rem). */
#services {
  scroll-margin-top: var(--header-height);
}

/* One-shot flash to draw attention to the deep-linked section (analogous to
   row-saved-flash in ServiceRow.vue). Triggered by useSectionDeepLink. */
@keyframes section-flash {
  0%,
  100% {
    box-shadow: none;
  }

  30% {
    box-shadow: 0 0 0 3px var(--ring);
  }
}

.section-flash {
  animation: section-flash 0.8s ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .section-flash {
    animation: none;
  }
}
</style>
